/**
 * Step 3: Position Sizing & Risk Management
 *
 * - Lot sizer selection (required)
 * - Risk manager selection (optional)
 * - Grid trading toggle (optional)
 * - Info icon popovers for tooltips
 */

import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import ParamInput from '../shared/ParamInput';
import InfoPopover from '../shared/InfoPopover';

const PositionSizingStep = ({ lotSizerComponents, riskManagerComponents, formData, updateFormData, errors }) => {
  // Select lot sizer
  const handleSelectLotSizer = (componentId) => {
    const component = lotSizerComponents.find(c => c.componentId === componentId);
    if (!component) {
      updateFormData({ lotSizer: null });
      return;
    }

    const defaultValues = {};
    component.parameters.forEach(param => {
      if (param.defaultValue !== undefined && param.defaultValue !== null) {
        defaultValues[param.name] = param.defaultValue;
      }
    });

    updateFormData({
      lotSizer: {
        componentId,
        component,
        parameters: component.parameters,
        values: defaultValues
      }
    });
  };

  // Select risk manager
  const handleSelectRiskManager = (componentId) => {
    if (!componentId) {
      updateFormData({ riskManager: null });
      return;
    }

    const component = riskManagerComponents.find(c => c.componentId === componentId);
    if (!component) {
      updateFormData({ riskManager: null });
      return;
    }

    const defaultValues = {};
    component.parameters.forEach(param => {
      if (param.defaultValue !== undefined && param.defaultValue !== null) {
        defaultValues[param.name] = param.defaultValue;
      }
    });

    updateFormData({
      riskManager: {
        componentId,
        component,
        parameters: component.parameters,
        values: defaultValues
      }
    });
  };

  // Update parameter value
  const handleParamChange = (componentType, paramName, value) => {
    const component = componentType === 'lotSizer' ? formData.lotSizer : formData.riskManager;
    if (!component) return;

    const updated = {
      ...component,
      values: { ...component.values, [paramName]: value }
    };

    updateFormData({ [componentType]: updated });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
        Position Sizing & Risk
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.6)' }}>
        Configure how positions are sized and manage risk.
      </Typography>

      {/* Lot Sizer - Required */}
      <Card
        sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: `1px solid ${errors.lotSizer ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
          borderRadius: '8px',
          mb: 3
        }}
      >
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2563eb', mb: 2 }}>
            Lot Sizer <span style={{ color: '#ef4444' }}>*</span>
          </Typography>

          {errors.lotSizer && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              {errors.lotSizer}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Select Lot Sizer</InputLabel>
            <Select
              value={formData.lotSizer?.componentId || ''}
              onChange={(e) => handleSelectLotSizer(e.target.value)}
              label="Select Lot Sizer"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' }
              }}
            >
              {lotSizerComponents.map(comp => (
                <MenuItem key={comp.componentId} value={comp.componentId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>{comp.name}</Typography>
                    {comp.tooltip && (
                      <Box sx={{ ml: 'auto' }}>
                        <InfoPopover
                          title={comp.name}
                          description={comp.tooltip}
                          tags={comp.tags}
                          accentColor="#2563eb"
                          iconSx={{ color: 'rgba(0,0,0,0.4)', '&:hover': { color: '#2563eb' } }}
                        />
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Parameters */}
          {formData.lotSizer && formData.lotSizer.parameters.length > 0 && (
            <Grid container spacing={2}>
              {formData.lotSizer.parameters.map(param => (
                <Grid item xs={12} sm={6} key={param.name}>
                  <ParamInput
                    param={param}
                    value={formData.lotSizer.values[param.name] ?? param.defaultValue ?? ''}
                    onChange={(value) => handleParamChange('lotSizer', param.name, value)}
                    accentColor="#2563eb"
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Risk Manager - Optional */}
      <Card
        sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px',
          mb: 3
        }}
      >
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#f59e0b', mb: 2 }}>
            Risk Manager <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>(Optional)</span>
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Select Risk Manager</InputLabel>
            <Select
              value={formData.riskManager?.componentId || ''}
              onChange={(e) => handleSelectRiskManager(e.target.value)}
              label="Select Risk Manager"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f59e0b' }
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {riskManagerComponents.map(comp => (
                <MenuItem key={comp.componentId} value={comp.componentId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography>{comp.name}</Typography>
                    {comp.tooltip && (
                      <Box sx={{ ml: 'auto' }}>
                        <InfoPopover
                          title={comp.name}
                          description={comp.tooltip}
                          tags={comp.tags}
                          accentColor="#f59e0b"
                          iconSx={{ color: 'rgba(0,0,0,0.4)', '&:hover': { color: '#f59e0b' } }}
                        />
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Parameters */}
          {formData.riskManager && formData.riskManager.parameters.length > 0 && (
            <Grid container spacing={2}>
              {formData.riskManager.parameters.map(param => (
                <Grid item xs={12} sm={6} key={param.name}>
                  <ParamInput
                    param={param}
                    value={formData.riskManager.values[param.name] ?? param.defaultValue ?? ''}
                    onChange={(value) => handleParamChange('riskManager', param.name, value)}
                    accentColor="#f59e0b"
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Grid Trading Toggle */}
      <Card
        sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px'
        }}
      >
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enableGrid}
                onChange={(e) => updateFormData({ enableGrid: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#8b5cf6' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8b5cf6' }
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                  Enable Grid Trading
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Add multiple entry levels with configurable spacing
                </Typography>
              </Box>
            }
          />

          {formData.enableGrid && (
            <Alert severity="info" sx={{ mt: 2, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
              Grid trading configuration will be available in a future update.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PositionSizingStep;
